import React from 'react';
import AsyncAutoComplete from './AsyncAutoComplete.jsx';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const RequirementGroup = ({
	groupIndex,
	documentTypeIds = [],
	dataSource,
	disabled,
	onChangeGroup,
	onRemoveGroup,
	isOnlyGroup,
}) => {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				padding: 1.5,
				border: '1px dashed',
				borderColor: 'grey.400',
				borderRadius: 1,
				marginBottom: 1,
			}}
		>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography variant="caption" sx={{ color: 'text.secondary', marginBottom: 0.5, display: 'block' }}>
					{`Group ${groupIndex + 1} — at least one needed`}
				</Typography>
				<AsyncAutoComplete
					name={`group-${groupIndex}`}
					value={documentTypeIds}
					values={{}}
					onChange={(e) => onChangeGroup(e.target.value)}
					dataSource={dataSource}
					multiple
					instantSearch
					label="Document types"
					disabled={disabled}
				/>
			</Box>
			<IconButton
				size="small"
				onClick={onRemoveGroup}
				disabled={disabled || isOnlyGroup}
			>
				<DeleteIcon fontSize="small" />
			</IconButton>
		</Box>
	);
};

const RequirementGroupsEditor = ({
	name,
	value = [[]],
	onChange,
	label,
	dataSource = '/api/data-sources/document-types/data',
	disabled = false,
}) => {
	const groups = Array.isArray(value) ? value : [[]];

	const fireChange = (newGroups) => {
		onChange({ target: { name, value: newGroups } });
	};

	const updateGroup = (groupIndex, newIds) => {
		const newGroups = [...groups];
		newGroups[groupIndex] = newIds;
		fireChange(newGroups);
	};

	const addGroup = () => {
		fireChange([...groups, []]);
	};

	const removeGroup = (groupIndex) => {
		fireChange(groups.filter((_, i) => i !== groupIndex));
	};

	return (
		<Box>
			{label && (
				<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', marginBottom: 1 }}>
					{label} — all groups must be satisfied (AND). Within each group, any document suffices (OR).
				</Typography>
			)}

			{groups.map((group, groupIndex) => (
				<RequirementGroup
					key={groupIndex}
					groupIndex={groupIndex}
					documentTypeIds={group}
					dataSource={dataSource}
					disabled={disabled}
					onChangeGroup={(newIds) => updateGroup(groupIndex, newIds)}
					onRemoveGroup={() => removeGroup(groupIndex)}
					isOnlyGroup={groups.length <= 1}
				/>
			))}

			<Button
				size="small"
				startIcon={<AddIcon />}
				onClick={addGroup}
				disabled={disabled}
				sx={{ marginTop: 0.5 }}
			>
				Add requirement group
			</Button>
		</Box>
	);
};

RequirementGroupsEditor.type = 'RequirementGroupsEditor';

RequirementGroupsEditor.spec = [{
	name: 'RequirementGroupsEditor',
	key: 'RequirementGroupsEditor',
	category: 'input',
	hidden: true,
	title: 'Requirement Groups Editor',
	fields: [],
}];

export default RequirementGroupsEditor;
